import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';

const SLIDES = [
  {
    icon: '🏏',
    title: 'Welcome to IPLTrade!',
    subtitle: 'The free IPL fantasy stock market',
    content: 'Trade IPL player stocks based on real match performance. The better a player performs, the higher their stock price rises. Buy low, sell high — just like a real stock market!',
    color: '#f59e0b',
    visual: 'welcome',
  },
  {
    icon: '⬡',
    title: 'Your Starting Balance',
    subtitle: '2000 points — use them wisely',
    content: 'Every player starts with 2000 points. Use them to buy player stocks. Your total value can grow beyond 2000 if your players perform well — sell at the right time to lock in gains!',
    color: '#10b981',
    visual: 'balance',
  },
  {
    icon: '🛒',
    title: 'Order Bucket',
    subtitle: 'Plan your trades, execute together',
    content: 'Add multiple buy/sell orders to your bucket first. Review them, adjust as needed, then execute all at once. This helps you deploy your full 2000-point budget with better planning.',
    color: '#3b82f6',
    visual: 'bucket',
  },
  {
    icon: '📈',
    title: 'Player Prices',
    subtitle: 'Live scoring + auto-refresh',
    content: 'Prices move with live performance — runs, wickets, catches, strike rate, and economy. During live matches, trading is locked for fairness, but pricing can still update in the background. The app auto-refreshes frequently, so values usually update without manual reload.',
    color: '#8b5cf6',
    visual: 'prices',
  },
  {
    icon: '�',
    title: 'Bad Performance → Price Drop',
    subtitle: 'Penalties are just as real as bonuses',
    content: 'A poor outing tanks a player\'s price — not just during the match, but permanently after it ends. Ducks, blocking, and expensive spells all carry lasting consequences.',
    color: '#ef4444',
    visual: 'penalties',
  },
  {
    icon: '�🔁',
    title: 'Trade Limit',
    subtitle: '200 trades per season',
    content: 'You have 200 total buy+sell trades for the entire IPL season. Each buy counts as 1 trade, each sell counts as 1 trade. Use them wisely — they don\'t reset until next season!',
    color: '#ef4444',
    visual: 'trades',
  },
  {
    icon: '⚔️',
    title: 'Private Contests',
    subtitle: 'Challenge friends to head-to-head battles',
    content: 'Create a private contest for your office group or friends. Each contest has its own separate team and leaderboard. Invite friends, set rules, and compete for glory!',
    color: '#ec4899',
    visual: 'contests',
  },
  {
    icon: '🏆',
    title: 'Leaderboard',
    subtitle: 'Climb the ranks throughout IPL season',
    content: 'The global leaderboard ranks all players by total portfolio value. Top 100 earn Gold badge, 101-500 earn Silver, 501-1000 earn Bronze. Your rank is always visible.',
    color: '#fbbf24',
    visual: 'leaderboard',
  },
];

function SlideVisual({ type, color }) {
  const style = (bg, c) => ({
    width: 60, height: 60, borderRadius: 16,
    background: bg, color: c,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  });

  switch (type) {
    case 'welcome':
      return (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 16 }}>
          {['🏏','📈','⬡','🏆'].map((e, i) => (
            <div key={i} style={{ ...style('rgba(245,158,11,0.15)', color), width: 48, height: 48, borderRadius: 12, fontSize: '1.4rem' }}>{e}</div>
          ))}
        </div>
      );
    case 'balance':
      return (
        <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', fontWeight: 700, color: '#10b981' }}>2000</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>starting points</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10, fontSize: '0.75rem' }}>
            <span style={{ color: 'var(--red)' }}>🔴 Buy = -pts</span>
            <span style={{ color: '#10b981' }}>🟢 Sell = +pts</span>
          </div>
        </div>
      );
    case 'bucket':
      return (
        <div style={{ marginBottom: 16 }}>
          {[
            { label: 'BUY Virat Kohli', pts: '-205 pts', color: '#ef4444' },
            { label: 'BUY Jasprit Bumrah', pts: '-225 pts', color: '#ef4444' },
            { label: 'SELL Rohit Sharma', pts: '+172 pts', color: '#10b981' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 5, fontSize: '0.78rem' }}>
              <span style={{ color: 'var(--text-primary)' }}>{item.label}</span>
              <span style={{ color: item.color, fontWeight: 600 }}>{item.pts}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: 'rgba(245,158,11,0.1)', borderRadius: 8, border: '1px solid rgba(245,158,11,0.3)', fontSize: '0.82rem', fontWeight: 600 }}>
            <span>Execute All</span>
            <span style={{ color: 'var(--accent)' }}>Net: -258 pts</span>
          </div>
        </div>
      );
    case 'penalties':
      return (
        <div style={{ marginBottom: 16 }}>
          {[
            { label: '🏏 Golden duck (1st ball)',    change: '−13%', color: '#ef4444' },
            { label: '🏏 Duck (0 runs)',             change: '−10%', color: '#ef4444' },
            { label: '🏏 Block fest (SR < 60)',      change: '−12%', color: '#ef4444' },
            { label: '🎳 Hammered (Eco > 13)',       change: '−13%', color: '#ef4444' },
            { label: '🎳 Expensive wicketless',      change: '−9%',  color: '#f97316' },
            { label: '🔴 Post-match permanent cut',  change: 'sticks to next match', color: '#dc2626' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', background: 'var(--bg-secondary)', borderRadius: 7, marginBottom: 4, fontSize: '0.76rem' }}>
              <span style={{ color: 'var(--text-primary)' }}>{item.label}</span>
              <span style={{ color: item.color, fontWeight: 700 }}>{item.change}</span>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function RulesModal({ onClose }) {
  const [slide, setSlide] = useState(0);
  const [closing, setClosing] = useState(false);
  const current = SLIDES[slide];
  const isLast  = slide === SLIDES.length - 1;

  const handleClose = () => {
    setClosing(true);
    sessionStorage.setItem('rules_shown', '1');
    onClose();
  };

  const next = () => {
    if (isLast) handleClose();
    else setSlide(s => s + 1);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 20, width: '100%', maxWidth: 440,
        overflow: 'hidden', position: 'relative',
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: 'var(--bg-secondary)' }}>
          <div style={{
            height: '100%', background: current.color,
            width: `${((slide + 1) / SLIDES.length) * 100}%`,
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Skip button */}
        <button
          onClick={handleClose}
          style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
        >
          <X size={16} />
        </button>

        {/* Content */}
        <div style={{ padding: '1.5rem 1.5rem 1rem' }}>
          {/* Slide counter */}
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 16 }}>
            {slide + 1} of {SLIDES.length}
          </div>

          {/* Icon */}
          <div style={{ fontSize: '2.5rem', marginBottom: 12, textAlign: 'center' }}>
            {current.icon}
          </div>

          {/* Visual */}
          <SlideVisual type={current.visual} color={current.color} />

          {/* Text */}
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, textAlign: 'center' }}>
            {current.title}
          </h2>
          <p style={{ fontSize: '0.82rem', color: current.color, fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>
            {current.subtitle}
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.65, textAlign: 'center' }}>
            {current.content}
          </p>
        </div>

        {/* Navigation */}
        <div style={{ padding: '0 1.5rem 1.5rem', display: 'flex', gap: 8, alignItems: 'center' }}>
          {slide > 0 && (
            <button
              onClick={() => setSlide(s => s - 1)}
              style={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <button
            onClick={next}
            style={{
              flex: 1, padding: '11px', borderRadius: 12, border: 'none',
              background: current.color, color: '#fff',
              fontFamily: 'var(--font-display)', fontSize: '0.9rem', fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'opacity 0.15s',
            }}
          >
            {isLast ? "Let's Trade! 🏏" : <><span>Next</span><ChevronRight size={16} /></>}
          </button>
        </div>

        {/* Dot indicators */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 5, paddingBottom: 16 }}>
          {SLIDES.map((_, i) => (
            <div
              key={i}
              onClick={() => setSlide(i)}
              style={{
                width: i === slide ? 20 : 6, height: 6, borderRadius: 3,
                background: i === slide ? current.color : 'var(--bg-elevated)',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
